import React from 'react';
import PropTypes from 'prop-types';
import { createInstance, OptimizelyProvider } from '@optimizely/react-sdk';
import { ConfigurationManager } from 'shell/utils/config';
import { FeatureToggleContextProvider } from 'shell/utils/toggles';
import { BRAND_PROFILES_LIST } from 'constants';

/**
 * Initializes Optimizely SDK with supplied configuration and renders <OptimizelyProvider />
 *
 * @param config
 * @param logger
 * @param userId
 * @param brand
 * @param userADGroup
 * @param children
 * @returns {*}
 * @constructor
 */
export const FeatureTogglesProvider = ({ config, logger, userId, brand, userADGroup, children }) => {
  const [optimizely, setOptimizely] = React.useState();
  const [error, setError] = React.useState(); // optimizely data loading failure
  const sdkKey = config.get('optimizely.sdkKey');

  React.useEffect(() => {
    const optimizelyInstance = createInstance({
      sdkKey: sdkKey[brand.toLowerCase()],
      logLevel: 4,
    });

    // Wait until Optimizely loads the datafile
    // https://github.com/optimizely/react-sdk#load-the-datafile-asynchronously
    optimizelyInstance.client.projectConfigManager.onReady().then(({ success, reason }) => {
      if (success) {
        setOptimizely(optimizelyInstance);
      } else {
        setError(reason);
      }
    });
  }, []);

  if (error) {
    logger.error(error, { originService: 'optimizely' });
    return <h1>Failed to load feature toggles data (Optmizely)</h1>;
  }

  if (!optimizely) {
    return null;
  }

  const toggles = JSON.parse(window.localStorage.getItem('toggles'));

  return (
    <OptimizelyProvider optimizely={optimizely} user={{ id: userId, attributes: { ad_groups: userADGroup } }}>
      <FeatureToggleContextProvider value={toggles}>{children}</FeatureToggleContextProvider>
    </OptimizelyProvider>
  );
};

FeatureTogglesProvider.propTypes = {
  config: PropTypes.instanceOf(ConfigurationManager).isRequired,
  logger: PropTypes.shape({}).isRequired,
  userId: PropTypes.string.isRequired,
  userADGroup: PropTypes.string,
  brand: PropTypes.oneOf(BRAND_PROFILES_LIST),
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};

FeatureTogglesProvider.defaultProps = {
  brand: BRAND_PROFILES_LIST[0],
};

export default FeatureTogglesProvider;
